import type { GradingScale } from "@/components/assignments/work/scale/types";
/**
 * One piece of this session's work inside Review: its head, one row per
 * learner, the score column, the open attempt with its judgement, and the
 * batch sheets. Every write goes through b8a's batch route, so one learner's
 * refusal never costs another's record and each answer names its own result.
 */
import { useCallback, useState, type ReactNode } from "react";
import { WorkBoxes } from "@/components/assignments/coursework/WorkBoxHost";
import type { Box } from "@/components/ui/box.schema";

import type { TeachingSessionAssignment } from "@/lib/api/sessionPortal";
import { useReviewWorkModels } from "./use-review-work-models";

export interface ReviewWorkState {
  /** The review answer has been read (or has failed); before that nothing folds. */
  read: boolean;
  /** Something here waits for the teacher: an attempt, a draft, a held judgement, a failure. */
  waiting: boolean;
  /** The work's foot has an act the page may rank. */
  candidate: boolean;
  /** A row the teacher opened owns the page's act, even over Close. */
  ownsAct: boolean;
  toReview: number;
  receiptFresh: boolean;
}

/** The terms Review needs, whether opened from a session or the course register. */
export type ReviewWorkRecord = Pick<TeachingSessionAssignment, "id" | "title" | "points" | "due_at" | "assessment_kind" | "external_url" | "rubric" | "status" | "grade_item_label"> & { course_status?: string; scale?: GradingScale | null; archived_at?: string | null; policy_values?: { revision_days: number } | null };

export interface ReviewWorkProps {
  work: ReviewWorkRecord;
  timezone: string;
  termOpen: boolean;
  /** Roll marks by student, the in-class evidence beside each row. */
  roll: Map<string, string | null> | null;
  /** This work holds the page's one filled act. */
  ranked: boolean;
  /** Close ranks first, so nothing opens by itself. */
  quietOpen: boolean;
  /** Bumped each time the teacher follows Prepare's "n to review": the oldest waiting attempt opens. */
  linkKey: number;
  onState: (id: string, state: ReviewWorkState) => void;
  /** Coursework's selected learner opens in the same judging flow. */
  focusEnrollmentId?: string | null;
  /** Explicit original attempt read, including a held judgement followed by
   * a newer attempt. The server resolves this exact row; the client never joins it. */
  exactSubmissionId?: string;
  /** Draws the boxes elsewhere, such as in the session's own Bento, which reports its rows opening back. */
  renderBoxes?: (boxes: Box[], onRowOpenChange: (box: string, row: string, open: boolean) => void) => ReactNode;
  /** The owning page's server projection, never derived from a boolean. */
  rank?: number | null;
}

export function ReviewWork(props: ReviewWorkProps) {
  const [opened, setOpened] = useState(false);
  const boxes = useReviewWorkModels({ ...props, opened });
  const onRowOpenChange = useCallback((box: string, _row: string, open: boolean) => {
    if (box === `review-${props.work.id}`) setOpened(open);
  }, [props.work.id]);
  if (props.renderBoxes) return props.renderBoxes(boxes, onRowOpenChange);
  return <section data-review-work={props.work.id}><WorkBoxes boxes={boxes} journey="review" onRowOpenChange={onRowOpenChange} /></section>;
}
